/**
 * Raffle viewer page for Mini App users
 * Mobile-friendly view of the raffle with personal winner celebration
 */

import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { getAppContext, haptic, getTelegram, type AppContext } from "@/lib/telegram";
import { useRaffleSocket } from "./hooks/useRaffleSocket";
import { TicketRoller } from "./components/TicketRoller";
import { PrizeDisplay } from "./components/PrizeDisplay";
import { ReactionBurst } from "./components/ReactionBurst";
import { ReactionBar } from "./components/ReactionBar";
import { RaffleHeader } from "./components/RaffleHeader";
import type { PrizeInfo, ReactionEmoji } from "@/lib/websocket";
import styles from "./RaffleViewerPage.module.css";
import "./raffle-theme.css";

// =============================================================================
// Personal Winner Celebration Component
// =============================================================================

interface WinnerCelebrationProps {
  prize: PrizeInfo;
  ticketNumber: string;
  onDismiss: () => void;
}

function WinnerCelebration({ prize, ticketNumber, onDismiss }: WinnerCelebrationProps) {
  // Extra strong and long vibration for winner (5x longer than before)
  useEffect(() => {
    // Initial celebration haptics
    haptic("success");
    haptic("heavy");
    
    // Extended vibration pattern for PWA/browser - 5x longer celebration!
    if (navigator.vibrate) {
      // Pattern: long-short-long-short-long-short-extra_long (repeated)
      navigator.vibrate([
        500, 150, 500, 150, 500, 150, 800, // First wave
        200, 100, 500, 150, 500, 150, 800, // Second wave
        200, 100, 400, 100, 400, 100, 600, // Third wave
        200, 100, 300, 100, 300, 100, 1000, // Fourth wave - crescendo
        200, 100, 200, 100, 200, 100, 500, // Fifth wave - fade out
      ]);
    }
    
    // Extended haptic feedback sequence for Telegram Mini App
    const timers: NodeJS.Timeout[] = [];
    
    // First second - intense
    timers.push(setTimeout(() => haptic("heavy"), 300));
    timers.push(setTimeout(() => haptic("heavy"), 600));
    timers.push(setTimeout(() => haptic("success"), 900));
    
    // Second second
    timers.push(setTimeout(() => haptic("heavy"), 1200));
    timers.push(setTimeout(() => haptic("heavy"), 1500));
    timers.push(setTimeout(() => haptic("success"), 1800));
    
    // Third second
    timers.push(setTimeout(() => haptic("heavy"), 2100));
    timers.push(setTimeout(() => haptic("heavy"), 2400));
    timers.push(setTimeout(() => haptic("success"), 2700));
    
    // Fourth second
    timers.push(setTimeout(() => haptic("heavy"), 3000));
    timers.push(setTimeout(() => haptic("success"), 3300));
    timers.push(setTimeout(() => haptic("heavy"), 3600));
    
    // Fifth second - finale
    timers.push(setTimeout(() => haptic("success"), 3900));
    timers.push(setTimeout(() => haptic("heavy"), 4200));
    timers.push(setTimeout(() => haptic("success"), 4500));
    
    return () => {
      timers.forEach(clearTimeout);
      if (navigator.vibrate) {
        navigator.vibrate(0); // Stop vibration on unmount
      }
    };
  }, []);

  return (
    <div className={styles.celebration} onClick={onDismiss}>
      {/* Confetti animation */}
      <div className={styles.confettiContainer}>
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className={styles.confetti}
            style={{
              "--delay": `${Math.random() * 2}s`,
              "--x": `${Math.random() * 100}vw`,
              "--rotation": `${Math.random() * 360}deg`,
              "--color": ["#f6e05e", "#4fd1c5", "#ed64a6", "#667eea", "#48bb78"][
                Math.floor(Math.random() * 5)
              ],
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* Content */}
      <div className={styles.celebrationContent}>
        {/* Animated trophy icon instead of emoji */}
        <div className={styles.celebrationIcon}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 15C8.68629 15 6 12.3137 6 9V3H18V9C18 12.3137 15.3137 15 12 15Z" fill="url(#trophy-gradient)" stroke="url(#trophy-stroke)" strokeWidth="1.5"/>
            <path d="M6 5H3V7C3 8.65685 4.34315 10 6 10" stroke="url(#trophy-stroke)" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M18 5H21V7C21 8.65685 19.6569 10 18 10" stroke="url(#trophy-stroke)" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M8 21H16" stroke="url(#trophy-stroke)" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M12 15V18M12 18H8V21H16V18H12Z" stroke="url(#trophy-stroke)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="trophy-gradient" x1="6" y1="3" x2="18" y2="15" gradientUnits="userSpaceOnUse">
                <stop stopColor="#f6e05e"/>
                <stop offset="1" stopColor="#ecc94b"/>
              </linearGradient>
              <linearGradient id="trophy-stroke" x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                <stop stopColor="#f6e05e"/>
                <stop offset="1" stopColor="#d69e2e"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1 className={styles.celebrationTitle}>ВЫ ВЫИГРАЛИ!</h1>

        {/* Prize */}
        <div className={styles.celebrationPrize}>
          {prize.imageUrl && (
            <img
              src={prize.imageUrl}
              alt={prize.name}
              className={styles.celebrationPrizeImage}
            />
          )}
          <div className={styles.celebrationPrizeName}>{prize.name}</div>
          {prize.description && (
            <div className={styles.celebrationPrizeDescription}>
              {prize.description}
            </div>
          )}
        </div>

        {/* Ticket info */}
        <div className={styles.celebrationTicket}>
          Ваш билет <strong>#{ticketNumber}</strong>
        </div>

        {/* Instructions */}
        <div className={styles.celebrationInstructions}>
          Подойдите к организаторам для получения приза
        </div>

        <button className={styles.celebrationDismiss}>
          Понятно
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function RaffleViewerPage() {
  const { raffleId } = useParams<{ raffleId: string }>();
  const actualRaffleId = raffleId ?? "";
  const { account } = useAuth();
  const navigate = useNavigate();
  const tg = getTelegram();

  // Determine app context once on mount (miniapp/pwa/browser)
  const appContext = useMemo<AppContext>(() => getAppContext(), []);

  // Ref for logo element to get its actual position from DOM
  const logoRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update CSS variables for rays position based on actual logo DOM position
  const updateRaysPosition = useCallback(() => {
    if (!logoRef.current || !containerRef.current) return;
    
    const rect = logoRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    containerRef.current.style.setProperty('--logo-center-x', `${centerX}px`);
    containerRef.current.style.setProperty('--logo-center-y', `${centerY}px`);
  }, []);

  // Update position on mount, resize, and orientation change
  useEffect(() => {
    // Initial update after render
    const timer = setTimeout(updateRaysPosition, 50);
    
    // Update on resize/orientation
    window.addEventListener('resize', updateRaysPosition);
    window.addEventListener('orientationchange', updateRaysPosition);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateRaysPosition);
      window.removeEventListener('orientationchange', updateRaysPosition);
    };
  }, [updateRaysPosition]);

  // Hide global rays (from index.css) on this page - we have our own dark-themed rays
  useEffect(() => {
    document.documentElement.setAttribute('data-raffle-page', 'true');
    return () => {
      document.documentElement.removeAttribute('data-raffle-page');
    };
  }, []);

  // Telegram BackButton for Mini App navigation
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Fallback to home if no history
        navigate('/');
      }
    };
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Prevent screen from sleeping during raffle viewing
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && navigator.wakeLock) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        // Wake lock request failed (e.g., low battery, not supported in WebView)
        console.debug('Wake Lock not available:', err);
      }
    };

    // Request wake lock on mount
    requestWakeLock();

    // Re-acquire wake lock when page becomes visible again
    // (wake lock is automatically released when tab/app goes to background)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release();
    };
  }, []);

  // Local state
  const [showPersonalWin, setShowPersonalWin] = useState(false);
  const [personalWinPrize, setPersonalWinPrize] = useState<PrizeInfo | null>(null);
  const [personalWinTicket, setPersonalWinTicket] = useState<string | null>(null);

  // WebSocket connection with accountId for personal winner detection
  const {
    isConnected,
    isJoined,
    error,
    raffle,
    currentPrize,
    prizes,
    ticketCount,
    completedDraws,
    activeRaffleId,
    isDrawing,
    drawingPrize,
    currentTicketNumber,
    drawProgress,
    ticketNumbers,
    lastWinner,
    isCompleted,
    showCompletedConfirmed,
    isWinner,
    wonPrize,
    resetWinnerState,
    sendReaction,
    reactionBurst,
    reactionTrigger,
    confirmAnimationComplete,
  } = useRaffleSocket({
    raffleId: actualRaffleId,
    accountId: account?.id,
    onWin: (prize, ticketNumber) => {
      setPersonalWinPrize(prize);
      setPersonalWinTicket(ticketNumber);
      setShowPersonalWin(true);
      // Strong vibration for personal winner is handled in WinnerCelebration
    },
    onDrawResult: () => {
      // Strong vibration for all viewers when winner is announced
      // (similar to old winner vibration)
      haptic("success");
      
      // Vibration pattern for PWA/browser
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 400, 100, 600]);
      }
      
      // Repeat haptic for celebration effect
      setTimeout(() => haptic("heavy"), 300);
      setTimeout(() => haptic("heavy"), 600);
      setTimeout(() => haptic("success"), 900);
    },
  });

  // Redirect to active raffle if this one is completed AND there's another active raffle
  // If no active raffle exists, stay on current page and show completed results
  useEffect(() => {
    if (
      raffle?.status === "COMPLETED" &&
      activeRaffleId &&
      activeRaffleId !== actualRaffleId
    ) {
      navigate(`/raffle/${activeRaffleId}`, { replace: true });
    }
    // Note: if activeRaffleId is null/empty, we stay on this page and show final results
  }, [raffle?.status, activeRaffleId, actualRaffleId, navigate]);

  // Show personal win celebration when isWinner becomes true
  // This handles both real-time wins and reconnection scenarios
  useEffect(() => {
    if (isWinner && wonPrize && !showPersonalWin) {
      setPersonalWinPrize(wonPrize);
      // Find the winning ticket number from completedDraws
      const myWinDraw = completedDraws.find(
        (draw) => draw.prizeName === wonPrize.name && draw.prizeId === wonPrize.id
      );
      if (myWinDraw) {
        setPersonalWinTicket(myWinDraw.ticketNumber);
      }
      setShowPersonalWin(true);
    }
  }, [isWinner, wonPrize, showPersonalWin, completedDraws]);

  // Handle dismiss of personal win celebration
  const dismissCelebration = useCallback(() => {
    setShowPersonalWin(false);
    resetWinnerState();
  }, [resetWinnerState]);

  // Handle reaction button press
  const handleReaction = useCallback((emoji: ReactionEmoji) => {
    sendReaction(emoji);
  }, [sendReaction]);

  // Calculate progress
  const drawnCount = completedDraws.length;
  const totalPrizes = prizes.length;
  const currentPrizeNumber = drawnCount + 1;

  // Determine view state
  const displayPrize = isDrawing ? drawingPrize : currentPrize;

  // Render content based on state
  const renderContent = () => {
    // Connection/error state
    if (!isConnected || error) {
      return (
        <div className={styles.statusContainer}>
          {!isConnected ? (
            <>
              <div className={styles.statusIconAnimated}>
                <span className={styles.pulseRing} />
                <span className={styles.pulseDot} />
              </div>
              <div className={styles.statusText}>Подключение к розыгрышу...</div>
            </>
          ) : (
            <>
              <div className={styles.statusIconError}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="16" r="1" fill="currentColor"/>
                </svg>
              </div>
              <div className={styles.statusText}>{error}</div>
            </>
          )}
        </div>
      );
    }

    // Loading state
    if (!raffle || !isJoined) {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.spinner} />
          <div className={styles.statusText}>Загрузка розыгрыша...</div>
        </div>
      );
    }

    // Waiting state (before raffle starts)
    // Note: raffle name is already shown in logoHeader, so we don't duplicate it here
    if (raffle.status === "DRAFT" || raffle.status === "READY") {
      return (
        <div className={styles.waitingContainer}>
          {/* Animated gift icon */}
          <div className={styles.waitingIconAnimated}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.giftIcon}>
              <rect x="3" y="10" width="18" height="11" rx="2" fill="url(#gift-box)" stroke="var(--raffle-primary)" strokeWidth="1.5"/>
              <rect x="1" y="7" width="22" height="5" rx="1.5" fill="url(#gift-ribbon)" stroke="var(--raffle-primary)" strokeWidth="1.5"/>
              <path d="M12 7V21" stroke="var(--raffle-accent)" strokeWidth="2"/>
              <path d="M12 7C12 7 12 4 9 4C6 4 6 7 9 7" stroke="var(--raffle-accent)" strokeWidth="1.5" fill="none"/>
              <path d="M12 7C12 7 12 4 15 4C18 4 18 7 15 7" stroke="var(--raffle-accent)" strokeWidth="1.5" fill="none"/>
              <defs>
                <linearGradient id="gift-box" x1="3" y1="10" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.2)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.2)"/>
                </linearGradient>
                <linearGradient id="gift-ribbon" x1="1" y1="7" x2="23" y2="12" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.3)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.3)"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          {raffle.description && (
            <p className={styles.waitingDescription}>{raffle.description}</p>
          )}
          
          <div className={styles.waitingStats}>
            <div className={styles.waitingStatCard}>
              <span className={styles.waitingStatValue}>{totalPrizes}</span>
              <span className={styles.waitingStatLabel}>Призов</span>
            </div>
            <div className={styles.waitingStatCard}>
              <span className={styles.waitingStatValue}>{ticketCount}</span>
              <span className={styles.waitingStatLabel}>Участников</span>
            </div>
          </div>
          
          {raffle.status === "READY" && (
            <div className={styles.waitingReady}>
              <span className={styles.waitingReadyPulse} />
              <span className={styles.waitingReadyText}>Розыгрыш скоро начнётся</span>
            </div>
          )}
        </div>
      );
    }

    // Completed state - show when raffle is completed OR when admin triggers final screen
    // Check both isCompleted (from WebSocket state) and raffle.status (from initial load)
    if (isCompleted || showCompletedConfirmed || raffle.status === "COMPLETED") {
      return (
        <>
          {/* Personal win celebration if won */}
          {showPersonalWin && personalWinPrize && personalWinTicket && (
            <WinnerCelebration
              prize={personalWinPrize}
              ticketNumber={personalWinTicket}
              onDismiss={dismissCelebration}
            />
          )}

          <div className={styles.completedContainer}>
            {/* Animated checkmark icon */}
            <div className={styles.completedIconAnimated}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="url(#completed-bg)" stroke="var(--raffle-success)" strokeWidth="2"/>
                <path d="M8 12L11 15L16 9" stroke="var(--raffle-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.checkPath}/>
                <defs>
                  <radialGradient id="completed-bg" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(12 12) rotate(90) scale(10)">
                    <stop stopColor="rgba(72, 187, 120, 0.2)"/>
                    <stop offset="1" stopColor="rgba(72, 187, 120, 0.05)"/>
                  </radialGradient>
                </defs>
              </svg>
            </div>
            <h1 className={styles.completedTitle}>Розыгрыш завершён!</h1>

            {/* Raffle description */}
            {raffle.description && (
              <p className={styles.completedDescription}>{raffle.description}</p>
            )}

            <div className={styles.winnersList}>
              {completedDraws.map((draw, index) => (
                <div key={draw.prizeId} className={styles.winnerCard}>
                  <div className={styles.winnerCardNumber}>{index + 1}</div>
                  <div className={styles.winnerCardContent}>
                    <div className={styles.winnerCardPrizeInfo}>
                      <div className={styles.winnerCardPrize}>{draw.prizeName}</div>
                      {draw.prizeDescription && (
                        <div className={styles.winnerCardPrizeDesc}>{draw.prizeDescription}</div>
                      )}
                    </div>
                    <div className={styles.winnerCardTicket}>#{draw.ticketNumber}</div>
                    <div className={styles.winnerCardWinner}>
                      {draw.winnerName}
                      {draw.winnerUsername && (
                        <span className={styles.winnerCardUsername}>
                          @{draw.winnerUsername}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    // Active raffle
    return (
      <>
        {/* Personal win celebration */}
        {showPersonalWin && personalWinPrize && personalWinTicket && (
          <WinnerCelebration
            prize={personalWinPrize}
            ticketNumber={personalWinTicket}
            onDismiss={dismissCelebration}
          />
        )}

        {/* Progress badge - no emoji, modern gradient badge */}
        <div className={styles.progressBadge}>
          <span className={styles.progressBadgeIcon}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="12" height="8" rx="1.5" fill="url(#badge-gift-box)" stroke="currentColor" strokeWidth="1"/>
              <rect x="1" y="4" width="14" height="3" rx="1" fill="url(#badge-gift-ribbon)" stroke="currentColor" strokeWidth="1"/>
              <path d="M8 4V14" stroke="currentColor" strokeWidth="1"/>
              <defs>
                <linearGradient id="badge-gift-box" x1="2" y1="6" x2="14" y2="14" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.3)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.3)"/>
                </linearGradient>
                <linearGradient id="badge-gift-ribbon" x1="1" y1="4" x2="15" y2="7" gradientUnits="userSpaceOnUse">
                  <stop stopColor="rgba(79, 209, 197, 0.4)"/>
                  <stop offset="1" stopColor="rgba(102, 126, 234, 0.4)"/>
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className={styles.progressBadgeText}>{currentPrizeNumber}/{totalPrizes}</span>
        </div>

        {/* Raffle description */}
        {raffle.description && (
          <p className={styles.activeDescription}>{raffle.description}</p>
        )}

        {/* Current prize */}
        <div className={styles.prizeSection}>
          <PrizeDisplay
            prize={displayPrize}
            isDrawing={isDrawing}
          />
        </div>

        {/* Ticket roller */}
        <div className={styles.rollerSection}>
          <TicketRoller
            currentNumber={currentTicketNumber}
            ticketNumbers={ticketNumbers}
            isSpinning={isDrawing}
            progress={drawProgress}
            winningNumber={lastWinner?.ticketNumber}
            onAnimationComplete={confirmAnimationComplete}
          />

          {/* Status text */}
          <div className={styles.status}>
            {isDrawing ? (
              <span className={styles.statusDrawing}>
                <span className={styles.statusDrawingDot} />
                Выбор победителя...
              </span>
            ) : lastWinner ? (
              <div className={styles.lastWinnerInfo}>
                <span className={styles.lastWinnerLabel}>Победитель:</span>
                <span className={styles.lastWinnerName}>{lastWinner.name}</span>
                {lastWinner.username && (
                  <span className={styles.lastWinnerUsername}>
                    @{lastWinner.username}
                  </span>
                )}
              </div>
            ) : (
              <span className={styles.statusWaiting}>
                <span className={styles.statusWaitingDot} />
                Ожидание запуска...
              </span>
            )}
          </div>
        </div>

        {/* Recent winners - collapsed by default, cleaner appearance */}
        {completedDraws.length > 0 && (
          <div className={styles.recentWinners}>
            <div className={styles.recentWinnersHeader}>
              <span className={styles.recentWinnersTitle}>Предыдущие победители</span>
              <span className={styles.recentWinnersCount}>{completedDraws.length}</span>
            </div>
            <div className={styles.recentWinnersList}>
              {completedDraws.slice().reverse().slice(0, 3).map((draw) => (
                <div key={draw.prizeId} className={styles.recentWinnerCard}>
                  <span className={styles.recentWinnerPrize}>{draw.prizeName}</span>
                  <span className={styles.recentWinnerTicket}>#{draw.ticketNumber}</span>
                  <span className={styles.recentWinnerName}>{draw.winnerName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </>
    );
  };

  // Check if reactions should be shown (on active raffle or during completion but not on connection/loading states)
  const showReactions = isConnected && isJoined && raffle && raffle.status !== "DRAFT";

  return (
    <div ref={containerRef} className={styles.container} data-context={appContext}>
      <RaffleHeader />
      
      {/* Logo + Title header - centered at top */}
      <div className={styles.logoHeader}>
        <img
          ref={logoRef}
          src="/logo.png"
          alt=""
          className={styles.logo}
          onLoad={updateRaysPosition}
        />
        {raffle?.name && (
          <div className={styles.raffleNameContainer}>
            <h1 className={styles.raffleName}>{raffle.name}</h1>
            {raffle.isTest && (
              <span className={styles.testBadge}>Тест</span>
            )}
          </div>
        )}
      </div>
      
      {/* Rays rendered via CSS ::before pseudo-element using --logo-center-x/y */}
      
      {renderContent()}

      {/* Reaction buttons - shown on all active states */}
      {showReactions && (
        <ReactionBar onReaction={handleReaction} disabled={!isJoined} />
      )}

      {/* Reaction bursts - rendered above everything */}
      {showReactions && (
        <ReactionBurst
          reactions={reactionBurst}
          trigger={reactionTrigger}
          position="right"
          size="medium"
        />
      )}
    </div>
  );
}

export default RaffleViewerPage;
